import React from 'react';
import {
  BlockStack,
  Card,
  ResourceList,
  Text,
  ResourceItem,
  InlineStack,
  Badge,
  Button
} from '@shopify/polaris';
import { DragHandleIcon, XIcon } from '@shopify/polaris-icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';

const Item = ({ id, title, status, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id
  });

  const tone = status === 'active' ? 'success' : status === 'draft' ? 'info' : undefined;

  const style = {
    ...(transform
      ? {
          transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          transition
        }
      : {}),
    zIndex: isDragging ? 1000 : 0,
    position: 'relative'
  };

  // Handle delete action properly
  const handleDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onDelete) {
      onDelete(id);
    }
  };

  return (
    <div style={style} ref={setNodeRef}>
      <ResourceItem
        id={id}
        accessibilityLabel={`Task: ${title}`}
        onClick={() => {}} // Empty onClick to prevent errors but not trigger anything
      >
        <InlineStack align='space-between' blockAlign='center'>
          <InlineStack gap='400' blockAlign='center'>
            <div
              {...attributes}
              {...listeners}
              onClick={(e) => e.stopPropagation()}
              className="itemAction"
              style={{ touchAction: 'none', cursor: 'grab' }} // Prevents page scrolling on mobile touch
            >
              <DragHandleIcon width='20' height='20' />
            </div>
            <Text variant='bodyMd' as='h3'>
              {title}
            </Text>
          </InlineStack>
          <InlineStack gap='400'>
            {status && (
              <Badge tone={tone} size='small'>
                {`${status.charAt(0).toUpperCase()}${status.slice(1)}`}
              </Badge>
            )}
            <Button
              icon={XIcon}
              variant='plain'
              tone="critical"
              onClick={handleDelete}
              accessibilityLabel={`Delete task ${title}`}
            />
          </InlineStack>
        </InlineStack>
      </ResourceItem>
    </div>
  );
};

export const SortableList = ({ items, setItems, onDelete, setIsDirty }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Add activation constraint to prevent unwanted drags
      activationConstraint: {
        distance: 5, // 5px movement required before activating drag
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // Handle item deletion
  const handleDeleteItem = (itemId) => {
    if (onDelete) {
      // Use the provided onDelete handler if available
      onDelete(itemId);
    } else {
      // Otherwise remove the item directly
      setItems((prevItems) => {
        const newItems = prevItems.filter(item => item.id !== itemId);
        return newItems;
      });
    }
    
    // Mark the form as dirty if we have that function
    if (setIsDirty) {
      setIsDirty(true);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      // Updates items in state
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const updatedItems = arrayMove(items, oldIndex, newIndex);

        // Mark form as dirty when items are reordered
        if (setIsDirty) {
          setIsDirty(true);
        }

        return updatedItems;
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >
      <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
        <ResourceList
          resourceName={{ singular: 'task', plural: 'tasks' }}
          items={items}
          renderItem={(item) => {
            return <Item {...item} onDelete={handleDeleteItem} />;
          }}
        />
      </SortableContext>
    </DndContext>
  );
};